import {

  actorHasJobSelectField,

  actorHasSiteSelectField,

  getDepartmentSelectUiMode,

  getJobsFromCsrj,

  getSitesForRequestContext,
  hasRequiredJobFieldForActor,
  insertSyntheticSiteSelectBeforeJob,

  isDepartmentRequestField,

  isSiteRequestField,

  needsSyntheticSiteSelect,

  resolveDepartmentSelectOptions,

  type FormSelectionFilterOptions,

} from "@/utils/formSelectionOptions";



type RequestFieldLike = {

  fieldElementName?: string;

  proctorProvided?: boolean;

  required?: boolean;

  synthetic?: boolean;

  sequence?: number;

};

type SiteSimpleLike = { id?: number; name?: string; [key: string]: unknown };

type RequestTestTypeLike = {
  companyTestType?: {
    id?: number;
    name?: string;
    active?: boolean;
    Active?: boolean;
    evalComparison?: number;
  } | null;
  sites?: SiteSimpleLike[];
  fields?: RequestFieldLike[];
  eligibleSitesForProctorSiteSelect?: SiteSimpleLike[];
  eligibleSitesForTakerSiteSelect?: SiteSimpleLike[];
  permissionRoleSitesForCreate?: SiteSimpleLike[];
  permissionRoleSitesForTake?: SiteSimpleLike[];
};

/** Company test types eligible for the request-page dropdown (company CTT active only). */
export function getActiveRequestTestTypes(
  testTypes: RequestTestTypeLike[] | null | undefined,
  options?: {
    excludeEvalComparisons?: number[] | null;
    includeEvalComparisons?: number[] | null;
  },
): RequestTestTypeLike[] {
  const toEvalSet = (values: number[] | null | undefined) => new Set(
    (values ?? [])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value)),
  );
  const excluded = toEvalSet(options?.excludeEvalComparisons);
  const included = toEvalSet(options?.includeEvalComparisons);
  return (testTypes ?? []).filter((tt) => {
    const ctt = tt?.companyTestType;
    if (!ctt) {
      return false;
    }
    const evalComparison = Number(ctt.evalComparison);
    if (included.size > 0 && !included.has(evalComparison)) {
      return false;
    }
    if (excluded.has(evalComparison)) {
      return false;
    }
    const active = ctt.active ?? ctt.Active;
    return active !== false;
  });
}

/** First active company test type with the given eval comparison, or null. */
export function findActiveRequestTestTypeByEvalComparison(
  testTypes: RequestTestTypeLike[] | null | undefined,
  evalComparison: number | null | undefined,
): RequestTestTypeLike | null {
  const target = Number(evalComparison);
  if (!Number.isFinite(target)) {
    return null;
  }
  return getActiveRequestTestTypes(testTypes).find(
    (tt) => Number(tt.companyTestType?.evalComparison) === target,
  ) ?? null;
}



function isFirstOrLastNameRequestField(field: RequestFieldLike | null | undefined): boolean {
  const name = String(field?.fieldElementName || "").trim().toLowerCase();
  return name === "first name" || name === "last name";
}

function getTesteeRequestFields(testState: Record<string, unknown> | null | undefined): RequestFieldLike[] {

  if (!testState || !Array.isArray(testState.fields)) {

    return [];

  }

  return testState.fields.filter(

    (field: RequestFieldLike) =>

      field.fieldElementName !== "Language" &&

      (
        // Always collect First/Last from the taker (including when proctor-provided).
        isFirstOrLastNameRequestField(field) ||
        (!field.proctorProvided && field.required)
      )

  );

}



function getFormSelectionFilterOptions(testState: Record<string, unknown>): FormSelectionFilterOptions {

  const testTypes = getActiveRequestTestTypes(
    testState.testTypes as RequestTestTypeLike[] | undefined,
  );

  const testData = testState.testData as { jobId?: number | null; siteId?: number | null } | undefined;

  const cttId = testTypes?.[0]?.companyTestType?.id;

  const testTypeDepartments = testState.testTypeDepartments as Array<{

    companyTestTypeId?: number;

    departmentId?: number;

  }> | undefined;

  const cttDepartmentIds = cttId && Array.isArray(testTypeDepartments)

    ? testTypeDepartments

        .filter((link) => Number(link.companyTestTypeId) === cttId)

        .map((link) => Number(link.departmentId))

        .filter((id) => id > 0)

    : [];

  return {

    companyTestTypeId: cttId,

    lockedJobId: testData?.jobId ?? null,

    lockedSiteId: testData?.siteId ?? null,

    cttDepartmentIds,

  };

}



function getActiveTestType(testState: Record<string, unknown>) {

  const testTypes = getActiveRequestTestTypes(
    testState.testTypes as RequestTestTypeLike[] | undefined,
  );

  const testData = testState.testData as { companyTestTypeId?: number | null } | undefined;

  const cttId = testData?.companyTestTypeId ?? testTypes?.[0]?.companyTestType?.id;

  if (!cttId || testTypes.length === 0) {

    return testTypes[0] ?? null;

  }

  return testTypes.find((tt) => Number((tt.companyTestType as { id?: number })?.id) === cttId) ?? testTypes[0] ?? null;

}



/** Mirrors Request.tsx field-hiding rules for site/job and pre-filled instance data. */

export function countVisibleTesteeRequestFields(testState: Record<string, unknown> | null | undefined): number {

  const fields = getTesteeRequestFields(testState);

  if (!fields.length || !testState) {

    return 0;

  }



  const filterOpts = getFormSelectionFilterOptions(testState);

  const testData = testState.testData as { jobId?: number | null; siteId?: number | null } | undefined;

  const activeTestType = getActiveTestType(testState);

  const sites = getSitesForRequestContext({

    siteFieldProctorProvided: false,

    testType: activeTestType,

    userRoleSites: testState.userAccessibleSites as Parameters<typeof getSitesForRequestContext>[0]["userRoleSites"],

    eligibleSitesFromServer: testState.eligibleSitesForTakerSiteSelect as Parameters<typeof getSitesForRequestContext>[0]["eligibleSitesFromServer"],

    companySiteRoleJobs: testState.companySiteRoleJobs as Parameters<typeof getSitesForRequestContext>[0]["companySiteRoleJobs"],

    filterOptions: filterOpts,

    requireSitesWithJobs: hasRequiredJobFieldForActor(fields, false),

    companyJobs: testState.jobs as Parameters<typeof getSitesForRequestContext>[0]["companyJobs"],

  });



  let visible = fields;

  const hasSiteField = fields.some(isSiteRequestField);

  if (hasSiteField && (sites.length < 2 || testData?.siteId)) {

    visible = visible.filter((field) => !isSiteRequestField(field));

  }



  if (needsSyntheticSiteSelect(fields, sites, false)) {

    visible = insertSyntheticSiteSelectBeforeJob(visible, false);

  }



  const hasDepartmentField = visible.some(isDepartmentRequestField);

  if (hasDepartmentField) {

    const activeFields = (activeTestType as { fields?: RequestFieldLike[] } | null)?.fields;

    const departments = resolveDepartmentSelectOptions({

      companyTestTypeId: filterOpts.companyTestTypeId,

      cttFields: activeFields,

      departments: testState.departments as Parameters<typeof resolveDepartmentSelectOptions>[0]["departments"],

      testTypeDepartments: testState.testTypeDepartments as Parameters<typeof resolveDepartmentSelectOptions>[0]["testTypeDepartments"],

      companySiteRoleJobs: testState.companySiteRoleJobs as Parameters<typeof resolveDepartmentSelectOptions>[0]["companySiteRoleJobs"],

      filterOptions: filterOpts,

      companyJobs: testState.jobs as Parameters<typeof resolveDepartmentSelectOptions>[0]["companyJobs"],

    });

    const deptField = visible.find(isDepartmentRequestField);

    const mode = getDepartmentSelectUiMode({

      hasCtt: Boolean(filterOpts.companyTestTypeId),

      optionCount: departments.length,

      required: Boolean(deptField?.required),

    });

    if (mode === "hidden" || mode === "single-locked") {

      visible = visible.filter((field) => !isDepartmentRequestField(field));

    }

  }



  const hasJobField = visible.some(

    (field) => field.fieldElementName === "Job Select" || field.fieldElementName === "Job"

  );

  if (hasJobField) {

    if (testData?.jobId) {

      visible = visible.filter(

        (field) => field.fieldElementName !== "Job Select" && field.fieldElementName !== "Job"

      );

    } else {

      const jobs = getJobsFromCsrj(

        testState.companySiteRoleJobs as Parameters<typeof getJobsFromCsrj>[0],

        filterOpts,

        testState.jobs as Parameters<typeof getJobsFromCsrj>[2]

      );

      if (jobs.length <= 1) {

        visible = visible.filter(

          (field) => field.fieldElementName !== "Job Select" && field.fieldElementName !== "Job"

        );

      }

    }

  }



  return visible.length;

}



export function shouldShowRequestPage(testState: Record<string, unknown> | null | undefined): boolean {

  if (!testState) {

    return false;

  }

  if (testState.promptForLanguage) {

    return true;

  }

  if (testState.requiresProctorData) {

    return true;

  }

  // requiresTesteeData is set server-side only when required testee fields are still unfilled.

  if (!testState.requiresTesteeData) {

    return false;

  }

  return countVisibleTesteeRequestFields(testState) > 0;

}



export function canAutoAdvanceTakerRequestPage(testState: Record<string, unknown> | null | undefined): boolean {

  if (!testState) {

    return false;

  }

  const fields = getTesteeRequestFields(testState);

  const filterOpts = getFormSelectionFilterOptions(testState);

  const activeTestType = getActiveTestType(testState);

  const sites = getSitesForRequestContext({

    siteFieldProctorProvided: false,

    testType: activeTestType,

    userRoleSites: testState.userAccessibleSites as Parameters<typeof getSitesForRequestContext>[0]["userRoleSites"],

    eligibleSitesFromServer: testState.eligibleSitesForTakerSiteSelect as Parameters<typeof getSitesForRequestContext>[0]["eligibleSitesFromServer"],

    companySiteRoleJobs: testState.companySiteRoleJobs as Parameters<typeof getSitesForRequestContext>[0]["companySiteRoleJobs"],

    filterOptions: filterOpts,

    requireSitesWithJobs: hasRequiredJobFieldForActor(fields, false),

    companyJobs: testState.jobs as Parameters<typeof getSitesForRequestContext>[0]["companyJobs"],

  });



  if (actorHasJobSelectField(fields, false) && sites.length === 0) {

    return false;

  }

  if (sites.length !== 1) {

    return false;

  }

  if (needsSyntheticSiteSelect(fields, sites, false)) {

    return false;

  }

  if (actorHasSiteSelectField(fields, false) && sites.length >= 2) {

    return false;

  }

  return countVisibleTesteeRequestFields(testState) === 0;

}


